import "../project.scss";
import "./ProjectListItem.scss";

import {
    Box,
    Icon,
    IconButton,
    Tooltip,
    Typography
} from "@husqvarna/ui-core";

import { useIntl } from "react-intl";
import { useState } from "react";
import ProjectOverview from "../models/ProjectOverview";
import { getTotalAreaForUnitSystem } from "../../shared/utils";
import ConfirmDelete from "../../shared/components/ConfirmDelete/ConfirmDelete";
import ProjectStatusTag from "./ProjectList/ProjectStatusTag";
import LatestUpdateText from "./ProjectList/LatestUpdateText";
import clsx from "clsx";
import { ReactComponent as DocumentIcon } from "../../shared/icons/document.svg";
import { ProjectType } from "../models/Enums";

export type ProjectListItemProps = {
  project: ProjectOverview;
  isSubItem?: boolean;
  isDragging: boolean;
  isActive: boolean;
  isOver?: boolean;
  onItemClick?: () => void;
  onEditItemClick?: (project:ProjectOverview) => void
  onDuplicateItemClick?: () => void;
  onDeleteItemClick?: (project: ProjectOverview) => void;
};

const ProjectListItem = ({
  project,
  isSubItem = false,
  isDragging,
  isActive,
  isOver = false,
  onItemClick,
  onEditItemClick,
  onDuplicateItemClick,
  onDeleteItemClick
}: ProjectListItemProps) => {
  const intl = useIntl();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = (project: ProjectOverview) => {
    setConfirmOpen(false);
    onDeleteItemClick?.call(this, project);
  }

  const wrapperClasses = clsx('tcsp-project-list__item-wrapper', {
    "tcsp-project-list__item-border": !isSubItem && !isDragging,
    "tcsp-project-list__item-disabled": isActive,
    "tcsp-project-list__item-dragging": isDragging,
    "tcsp-project-list__item-over": isOver,
  });

  const getProjectTypeName = (type: ProjectType) => {
    switch (type) {
      case ProjectType.Other:
        return intl.formatMessage({id: "projectTypeOption.Other.short", defaultMessage: "Other"});
      case ProjectType.FacilityManagement:
        return intl.formatMessage({id: "projectTypeOption.FacilityManagement.short", defaultMessage: "Facility"});
      case ProjectType.Golf:
        return intl.formatMessage({id: "projectTypeOption.Golf.short", defaultMessage: "Golf"});
      case ProjectType.HospitalityResort:
        return intl.formatMessage({id: "projectTypeOption.HospitalityResort.short", defaultMessage: "Hospitality"});
      case ProjectType.LandscapeContractor:
        return intl.formatMessage({id: "projectTypeOption.LandscapeContractor.short", defaultMessage: "Landscape"});
      case ProjectType.MunicipalityPublicSite:
        return intl.formatMessage({id: "projectTypeOption.MunicipalityPublicSite.short", defaultMessage: "Public"});
      case ProjectType.SchoolUniversity:
        return intl.formatMessage({id: "projectTypeOption.SchoolUniversity.short", defaultMessage: "School"});
      case ProjectType.Sport:
        return intl.formatMessage({id: "projectTypeOption.Sport.short", defaultMessage: "Sport"});
    }
  }

  return (
    <>
      <Box
        container
        alignItems="center"
        gap={3} >
          <Tooltip toolTipText={intl.formatMessage({id:"project.dragItem.tooltip", defaultMessage:"Drag to move"})} >
            <Box
              className="tcsp-project-list__draggable-handle"
              backgroundColor="Grey200"
              p={1}
              br={2}
              alignItems="center"
              justifyContent="center"
            >
                <Icon iconId="sort" size={4} />
            </Box>
          </Tooltip>
        <Box
          className={wrapperClasses}
          py={1} 
          mx={4}
          width={"full"}
          alignItems="center"
        >
          <Box ml={isSubItem ? 4 : -3} mr={-3} onClick={onItemClick} tabIndex={0} className="tcsp-project-list__item">
            <Box
              br={6} 
              alignItems="center" 
              py={4} 
              pl={2}
              pr={3}
              className="tcsp-project-list__item-inner"
            >
              <Box alignItems="center">
                <DocumentIcon height={24} width={24} className="tcsp-project-list__item-inner__icon" />
                <Typography ml={6} variant="Body1" text={project.name} textAlign="left" wordBreak="break-all" lineClamp={2} />
              </Box>
              <ProjectStatusTag projectStatus={project.status} />
              <Typography tag="span" textAlign="left" variant="Body1" text={getProjectTypeName(project.type)} className="project-type" />
              <Typography tag="span" textAlign="right" variant="Body1" text={project.areaCount.toString()} />
              <Typography tag="span" textAlign="right" variant="Body1" text={getTotalAreaForUnitSystem(project.totalAreaSize, project.region.unitSystem)} />
              <LatestUpdateText updatedWhen={project.updatedWhen} />
            </Box>
          </Box>
          {!isDragging && (
            <Box
              className="tcsp-project-list__item-actions"
              backgroundColor="White"
              br={3}
              flexDirection="row"
              gap={1}
            >
              <Tooltip toolTipText={intl.formatMessage({id: "project.itemAction.Edit.tooltip", defaultMessage: "Edit"})}>
                <IconButton size="Medium" icon="pen" onClick={() => onEditItemClick?.call(this, project)} />
              </Tooltip>
              <Tooltip toolTipText={intl.formatMessage({id: "project.itemAction.Duplicate.tooltip", defaultMessage: "Duplicate"})}>
                <IconButton size="Medium" icon="copy" onClick={onDuplicateItemClick} />
              </Tooltip>
              <Tooltip toolTipText={intl.formatMessage({id: "project.itemAction.Delete.tooltip", defaultMessage: "Delete"})}>
                <IconButton size="Medium" icon="trash" onClick={() => setConfirmOpen(true)} />
              </Tooltip>
            </Box>
          )}
          <ConfirmDelete isOpen={confirmOpen}
                    onConfirm={() => handleDelete(project)}
                    onCancel={() => setConfirmOpen(false)}
                    headingText={intl.formatMessage({id:"project.confirmDelete.title", defaultMessage:"You are about to delete a project"})}
                    bodyText={intl.formatMessage({id:"project.confirmDelete.message", defaultMessage:"Are you sure that you want to delete this project permanently?"})}
                    primaryButtonText={intl.formatMessage({id: "project.confirmDelete.confirmDelete", defaultMessage: "Delete project"})}
                  />
        </Box>
      </Box>
    </>
  )
}

export default ProjectListItem;
